import type { Conversation } from '@volt/shared';

export const DRAWER_SWIPE_OPEN_DISTANCE_PX = 90;
export const DRAWER_SWIPE_OPEN_VELOCITY = 0.3;
export const DRAWER_SWIPE_CLOSE_DISTANCE_PX = 90;
export const DRAWER_SWIPE_CLOSE_VELOCITY = 0.25;
export const DRAWER_SWIPE_MIN_DISTANCE_PX = 12;
export const LONG_PRESS_MOVE_TOLERANCE_PX = 10;
export const CONTEXT_MENU_CLOSE_SWIPE_DISTANCE_PX = 24;

export function reorderIds(ids: string[], sourceId: string, targetId: string) {
  const next = [...ids];
  const sourceIndex = next.indexOf(sourceId);
  const targetIndex = next.indexOf(targetId);
  if (sourceIndex === -1 || targetIndex === -1 || sourceIndex === targetIndex) return ids;
  const [moved] = next.splice(sourceIndex, 1);
  if (!moved) return ids;
  next.splice(targetIndex, 0, moved);
  return next;
}

export function movedPastLongPressTolerance(
  startX: number,
  startY: number,
  currentX: number,
  currentY: number,
  tolerance = LONG_PRESS_MOVE_TOLERANCE_PX,
) {
  return Math.hypot(currentX - startX, currentY - startY) > tolerance;
}

export function filterVisibleConversations(conversations: Conversation[], showArchived: boolean) {
  if (!showArchived) {
    // Normal view: hide archived conversations
    return conversations.filter((conversation) => !conversation.isArchived);
  }
  // Archive view: show all conversations (pinned + regular + archived)
  // so folders, pinned, and regular chats remain visible alongside archived ones
  return conversations;
}

export function getRecentConversations(conversations: Conversation[], limit = 10): Conversation[] {
  return [...conversations]
    .filter((conversation) => !conversation.isArchived && !conversation.isPinned)
    .sort((left, right) => {
      const leftTime = new Date(left.lastMessageAt ?? left.updatedAt).getTime();
      const rightTime = new Date(right.lastMessageAt ?? right.updatedAt).getTime();
      return rightTime - leftTime;
    })
    .slice(0, limit);
}

/** Allow drawer swipe from any horizontal position on the main screen. */
export const DRAWER_SWIPE_FROM_ANYWHERE = true;

export function shouldBeginDrawerSwipe(_startX: number) {
  return DRAWER_SWIPE_FROM_ANYWHERE;
}

export function getDrawerSwipeMetrics(
  startX: number,
  startY: number,
  currentX: number,
  currentY: number,
) {
  const horizontalDistance = Math.max(0, currentX - startX);
  const verticalDistance = Math.abs(currentY - startY);
  return {
    horizontalDistance,
    verticalDistance,
    isHorizontalGesture: horizontalDistance > 0 && horizontalDistance >= verticalDistance * 1.2,
  };
}

export function shouldCancelDrawerSwipe(horizontalDistance: number, verticalDistance: number) {
  return verticalDistance > 12 && verticalDistance > horizontalDistance;
}

export function drawerOpeningDirection(
  dx: number,
  dy: number,
  owner: 'left' | 'right' | null = null,
): 'left' | 'right' | null {
  if (owner) return owner;
  if (Math.abs(dx) < 12 || Math.abs(dx) < Math.abs(dy) * 1.2) return null;
  return dx > 0 ? 'left' : 'right';
}

export function shouldOpenDrawerSwipe(
  horizontalDistance: number,
  verticalDistance: number,
  durationMs: number,
) {
  if (horizontalDistance < DRAWER_SWIPE_MIN_DISTANCE_PX || horizontalDistance < verticalDistance) {
    return false;
  }
  const velocity = horizontalDistance / Math.max(durationMs, 1);
  return (
    horizontalDistance >= DRAWER_SWIPE_OPEN_DISTANCE_PX || velocity >= DRAWER_SWIPE_OPEN_VELOCITY
  );
}

export function shouldCloseDrawerSwipe(
  leftwardDistance: number,
  verticalDistance: number,
  durationMs: number,
): boolean {
  if (leftwardDistance < DRAWER_SWIPE_MIN_DISTANCE_PX || leftwardDistance < verticalDistance) {
    return false;
  }
  const velocity = leftwardDistance / Math.max(durationMs, 1);
  return (
    leftwardDistance >= DRAWER_SWIPE_CLOSE_DISTANCE_PX || velocity >= DRAWER_SWIPE_CLOSE_VELOCITY
  );
}

export function shouldCloseContextMenuOnSwipeRight(
  horizontalDistance: number,
  verticalDistance: number,
) {
  return (
    horizontalDistance >= CONTEXT_MENU_CLOSE_SWIPE_DISTANCE_PX &&
    horizontalDistance > verticalDistance
  );
}

export function lockScrollable(
  target: { style: { overflowY: string; touchAction: string } } | null,
) {
  if (!target) return () => undefined;
  const previousOverflowY = target.style.overflowY;
  const previousTouchAction = target.style.touchAction;
  target.style.overflowY = 'hidden';
  target.style.touchAction = 'none';
  return () => {
    target.style.overflowY = previousOverflowY;
    target.style.touchAction = previousTouchAction;
  };
}
